import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Check, Copy, MessageSquare, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface StepConfirmationProps {
  bookingType: "private" | "open_match";
  courtName: string;
  startTime: Date;
  endTime: Date;
  onClose: () => void;
  orgSettings?: Record<string, unknown> | null;
}

/** Replace simple {bana}, {tid}, {url} placeholders in a template string */
function applyTemplate(
  template: string,
  vars: { bana: string; tid: string; url: string }
): string {
  return template
    .replace(/\{bana\}/g, vars.bana)
    .replace(/\{tid\}/g, vars.tid)
    .replace(/\{url\}/g, vars.url);
}

export default function StepConfirmation({
  bookingType,
  courtName,
  startTime,
  endTime,
  onClose,
  orgSettings,
}: StepConfirmationProps) {
  const { t } = useTranslation("booking");
  const isOpen = bookingType === "open_match";
  const typeIcon = bookingType === "private" ? "🔒" : "🌍";
  const typeLabel =
    bookingType === "private" ? t("privateBooking") : t("openMatch");

  const timeStr = `${format(startTime, "EEE d MMM", { locale: sv })} ${format(startTime, "HH:mm")}–${format(endTime, "HH:mm")}`;
  // G-063: share the clubs's clean booking entry, not the stateful /book?date=…
  // grid URL. The message body already carries the booking info (type/court/time).
  // (A per-booking deep link would need bookingId threaded through BookingWizard
  // — T1-owned; flagged.)
  const shareUrl = `${window.location.origin}/book`;

  // Build share message based on booking type (with configurable override)
  const templateVars = { bana: courtName, tid: timeStr, url: shareUrl };

  const shareText = (() => {
    const settingsKey = isOpen
      ? "confirmation_share_message_open"
      : "confirmation_share_message_private";
    const customTemplate = orgSettings?.[settingsKey] as string | undefined;

    if (customTemplate) {
      return applyTemplate(customTemplate, templateVars);
    }

    // Default: open includes URL for inviting, private is just practical info
    const base = `${typeLabel}: ${courtName}, ${timeStr}`;
    return isOpen ? `${base}\n${shareUrl}` : base;
  })();

  // Heading text above share buttons
  const headingText = (() => {
    const settingsKey = isOpen
      ? "confirmation_share_text_open"
      : "confirmation_share_text_private";
    const custom = orgSettings?.[settingsKey] as string | undefined;
    if (custom) return custom;
    return isOpen ? t("inviteMore") : t("shareWithPlayers");
  })();

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success(t("linkCopied"));
    } catch {
      toast.error(t("couldNotCopy"));
    }
  };

  const handleShareSMS = () => {
    const body = encodeURIComponent(shareText);
    window.open(`sms:?body=${body}`, "_blank");
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(shareText);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <div className="flex flex-col items-center text-center space-y-6 py-8">
      {/* Success icon */}
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
        <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
      </div>

      <div className="space-y-1">
        <h2 className="text-xl font-bold">{t("bookingDone")}</h2>
      </div>

      {/* Booking summary */}
      <div className="space-y-0.5 text-sm text-muted-foreground">
        <p className="font-medium text-foreground">
          {typeIcon} {typeLabel}
        </p>
        <p>
          {courtName} &middot;{" "}
          {format(startTime, "EEE d MMM", { locale: sv })} &middot;{" "}
          {format(startTime, "HH:mm")}–{format(endTime, "HH:mm")}
        </p>
      </div>

      {/* Share buttons */}
      <div className="w-full max-w-xs space-y-2">
        <Button
          variant="outline"
          className="w-full gap-2 min-h-[44px]"
          onClick={handleCopyLink}
        >
          <Copy className="h-4 w-4" />
          {t("copyLink")}
        </Button>
        <Button
          variant="outline"
          className="w-full gap-2 min-h-[44px]"
          onClick={handleShareSMS}
        >
          <Smartphone className="h-4 w-4" />
          {t("shareViaSMS")}
        </Button>
        <Button
          variant="outline"
          className="w-full gap-2 min-h-[44px]"
          onClick={handleShareWhatsApp}
        >
          <MessageSquare className="h-4 w-4" />
          {t("shareViaWhatsApp")}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        {headingText}
      </p>

      <Button className="w-full max-w-xs min-h-[44px]" size="lg" onClick={onClose}>
        {t("done")}
      </Button>
    </div>
  );
}
